/* ==========================================================================
   HOPE BASE ADVISORY LLC — ADMIN PORTAL DASHBOARD & AUTHENTICATION
   ========================================================================== */

const AUTH_API = '/api/auth.php';
const INVOICE_API = '/api/invoices.php';

let isAdminAuthenticated = false;
let allDocumentsList = [];

export function initAdminPortal() {
  const adminPortalModal = document.getElementById('admin-portal-modal');
  const adminPortalClose = document.getElementById('admin-portal-close');
  const ownerFooterLink = document.getElementById('owner-portal-link');

  const loginView = document.getElementById('admin-login-view');
  const dashboardView = document.getElementById('admin-dashboard-view');

  const loginUsernameInput = document.getElementById('admin-user-input');
  const loginPasswordInput = document.getElementById('admin-pass-input');
  const loginSubmitBtn = document.getElementById('admin-login-submit');
  const loginErrorMsg = document.getElementById('admin-login-error');
  const logoutBtn = document.getElementById('admin-logout-btn');

  const searchInput = document.getElementById('admin-search-input');
  const filterTypeSelect = document.getElementById('admin-filter-type');
  const docTableBody = document.getElementById('admin-doc-table-body');

  // URL Hash Trigger (#admin or #invoice)
  function handleUrlHash() {
    if (window.location.hash === '#admin' || window.location.hash === '#invoice') {
      openAdminPortal();
    }
  }

  handleUrlHash();
  window.addEventListener('hashchange', handleUrlHash);

  // Global Link Trigger for any href="#admin"
  document.querySelectorAll('a[href="#admin"], a[href="#invoice"], #owner-portal-link').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      window.location.hash = '#admin';
      openAdminPortal();
    });
  });

  async function openAdminPortal() {
    if (!adminPortalModal) return;
    adminPortalModal.style.display = 'flex';
    requestAnimationFrame(() => {
      adminPortalModal.classList.add('active');
    });
    document.body.style.overflow = 'hidden';
    await checkAuthStatus();
  }

  function closeAdminPortal() {
    if (!adminPortalModal) return;
    adminPortalModal.classList.remove('active');
    setTimeout(() => {
      adminPortalModal.style.display = 'none';
    }, 200);
    document.body.style.overflow = '';
    if (window.location.hash === '#admin' || window.location.hash === '#invoice') {
      history.pushState(null, '', window.location.pathname + window.location.search);
    }
  }

  document.querySelectorAll('#admin-portal-close, #admin-login-close, #admin-dashboard-close').forEach(btn => {
    btn.addEventListener('click', closeAdminPortal);
  });

  // Backdrop click to close
  adminPortalModal?.addEventListener('click', (e) => {
    if (e.target === adminPortalModal) {
      closeAdminPortal();
    }
  });

  // Check Active Session with PHP Auth API
  async function checkAuthStatus() {
    try {
      const response = await fetch(`${AUTH_API}?action=check`);
      const result = await response.json();

      if (result.success && result.authenticated) {
        setAuthenticatedState(true, result.user);
      } else {
        setAuthenticatedState(false);
      }
    } catch (err) {
      // Fallback for local preview without active PHP backend session
      const localAuth = sessionStorage.getItem('hba_admin_auth') === 'true';
      setAuthenticatedState(localAuth, { username: 'admin' });
    }
  }

  function setAuthenticatedState(authenticated, user = null) {
    isAdminAuthenticated = authenticated;
    const container = document.getElementById('admin-portal-container') || adminPortalModal?.querySelector('.admin-portal-container');

    if (authenticated) {
      sessionStorage.setItem('hba_admin_auth', 'true');
      if (user) {
        sessionStorage.setItem('hba_admin_user', JSON.stringify(user));
      }
      container?.classList.remove('login-mode');
      if (loginView) loginView.style.display = 'none';
      if (dashboardView) dashboardView.style.display = 'block';

      const userBadge = document.getElementById('admin-user-display-name');
      if (userBadge) {
        const storedUser = user || JSON.parse(sessionStorage.getItem('hba_admin_user') || 'null');
        if (storedUser) {
          const display = storedUser.full_name && storedUser.full_name !== storedUser.username
            ? `${storedUser.full_name} (${storedUser.username})`
            : (storedUser.username || 'Authorized User');
          userBadge.textContent = display;
        }
      }

      // Clear credentials from memory/inputs
      if (loginUsernameInput) loginUsernameInput.value = '';
      if (loginPasswordInput) loginPasswordInput.value = '';

      // Parallel fetch: sync settings & load documents concurrently
      Promise.all([
        syncUserSignatureAndSettings(),
        loadDashboardDocuments()
      ]).catch(() => {});
    } else {
      sessionStorage.removeItem('hba_admin_auth');
      sessionStorage.removeItem('hba_admin_user');
      sessionStorage.removeItem('hba_docs_cache');
      localStorage.removeItem('hba_user_signature');
      localStorage.removeItem('hba_current_user');
      container?.classList.add('login-mode');
      if (loginUsernameInput) loginUsernameInput.value = '';
      if (loginPasswordInput) loginPasswordInput.value = '';
      if (loginView) loginView.style.display = 'block';
      if (dashboardView) dashboardView.style.display = 'none';
      if (loginUsernameInput) loginUsernameInput.focus();
    }
  }

  async function syncUserSignatureAndSettings() {
    try {
      const response = await fetch(`${SETTINGS_API}?action=get_settings`);
      const result = await response.json();
      if (result.success && result.data) {
        if (result.data._current_user) {
          const u = result.data._current_user;
          if (u.signature_data) {
            localStorage.setItem('hba_user_signature', u.signature_data);
          }
          localStorage.setItem('hba_current_user', JSON.stringify(u));
        }
        if (result.data.company_stamp) {
          localStorage.setItem('hba_company_stamp', result.data.company_stamp);
        }
      }
    } catch (err) {}
  }

  // Handle Login Submit
  if (loginSubmitBtn && loginUsernameInput && loginPasswordInput) {
    loginSubmitBtn.addEventListener('click', (e) => {
      e.preventDefault();
      performLogin();
    });

    loginPasswordInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        performLogin();
      }
    });
  }

  async function performLogin() {
    const username = loginUsernameInput.value.trim();
    const password = loginPasswordInput.value.trim();

    if (!username || !password) {
      showLoginError('Please enter both username and password.');
      return;
    }

    loginSubmitBtn.disabled = true;
    loginSubmitBtn.textContent = 'Verifying...';

    try {
      const response = await fetch(`${AUTH_API}?action=login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      const result = await response.json();

      if (result.success) {
        showLoginError('');
        setAuthenticatedState(true, result.user);
      } else {
        showLoginError(result.error || 'Invalid credentials');
      }
    } catch (err) {
      showLoginError('Invalid username or password.');
    } finally {
      loginSubmitBtn.disabled = false;
      loginSubmitBtn.textContent = 'Sign In to Dashboard';
    }
  }

  function showLoginError(msg) {
    if (!loginErrorMsg) return;
    if (msg) {
      loginErrorMsg.style.display = 'block';
      loginErrorMsg.textContent = msg;
    } else {
      loginErrorMsg.style.display = 'none';
    }
  }

  // Handle Logout Button (Instant 0ms UI switch with background session termination)
  if (logoutBtn) {
    logoutBtn.addEventListener('click', () => {
      setAuthenticatedState(false);
      fetch(`${AUTH_API}?action=logout`, { method: 'POST' }).catch(() => {});
    });
  }

  // Load All Documents for Dashboard Landing Page (Instant Stale-While-Revalidate)
  async function loadDashboardDocuments() {
    if (!docTableBody) return;

    // Check for cached documents for instant 0ms render
    const cachedDocs = sessionStorage.getItem('hba_docs_cache');
    if (cachedDocs) {
      try {
        const parsed = JSON.parse(cachedDocs);
        if (Array.isArray(parsed) && parsed.length > 0) {
          allDocumentsList = parsed;
          updateDashboardAnalytics(allDocumentsList);
          renderDashboardTable(allDocumentsList);
        }
      } catch (e) {}
    } else {
      docTableBody.innerHTML = '<tr><td colspan="7" style="text-align:center; padding:2rem; color:var(--color-text-muted);">Loading Database Documents...</td></tr>';
    }

    try {
      const response = await fetch(`${INVOICE_API}?action=list`);
      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        allDocumentsList = result.data;
        sessionStorage.setItem('hba_docs_cache', JSON.stringify(allDocumentsList));
      } else if (!allDocumentsList.length) {
        allDocumentsList = [];
      }
    } catch (err) {
      if (!allDocumentsList.length) {
        allDocumentsList = getLocalMockDocuments();
      }
    }

    updateDashboardAnalytics(allDocumentsList);
    renderDashboardTable(allDocumentsList);
  }

  function updateDashboardAnalytics(docs) {
    let totalRev = 0;
    let quoCount = 0;
    let invCount = 0;

    docs.forEach(doc => {
      const amt = parseFloat(doc.total_amount || 0);
      if (doc.doc_type === 'invoice') {
        invCount++;
        totalRev += amt;
      } else {
        quoCount++;
      }
    });

    const statTotalDocs = document.getElementById('stat-total-docs');
    const statQuoCount = document.getElementById('stat-quo-count');
    const statInvCount = document.getElementById('stat-inv-count');
    const statTotalRev = document.getElementById('stat-total-rev');

    if (statTotalDocs) statTotalDocs.textContent = docs.length;
    if (statQuoCount) statQuoCount.textContent = quoCount;
    if (statInvCount) statInvCount.textContent = invCount;
    if (statTotalRev) statTotalRev.textContent = `$${totalRev.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  }

  function renderDashboardTable(docs) {
    if (!docTableBody) return;

    const searchTerm = (searchInput?.value || '').toLowerCase();
    const filterType = filterTypeSelect?.value || 'all';

    const filtered = docs.filter(doc => {
      const matchType = filterType === 'all' || doc.doc_type === filterType;
      const matchSearch = (doc.doc_number || '').toLowerCase().includes(searchTerm) ||
                          (doc.client_name || '').toLowerCase().includes(searchTerm) ||
                          (doc.client_email || '').toLowerCase().includes(searchTerm);
      return matchType && matchSearch;
    });

    if (filtered.length === 0) {
      docTableBody.innerHTML = '<tr><td colspan="7" style="text-align:center; padding:2.5rem; color:var(--color-text-muted);">No matching documents found in database. Click + Create to generate a new quotation or invoice.</td></tr>';
      return;
    }

    docTableBody.innerHTML = '';
    filtered.forEach(doc => {
      const row = document.createElement('tr');
      row.className = 'admin-table-row';
      const isQuo = doc.doc_type === 'quotation';
      const badgeClass = isQuo ? 'pill-badge orange' : 'pill-badge teal';

      row.innerHTML = `
        <td class="cell-docnum" style="white-space: nowrap;"><strong style="color:var(--color-dark-text); font-weight:700; font-size:0.8125rem; letter-spacing:0.02em;">${escapeHtml(doc.doc_number)}</strong></td>
        <td class="cell-type" style="white-space: nowrap;"><span class="${badgeClass}" style="white-space: nowrap; display: inline-block;">${isQuo ? 'Quotation' : 'Tax Invoice'}</span></td>
        <td class="cell-client">
          <div style="font-weight:600; font-size:0.875rem; color:var(--color-dark-text); line-height:1.3;">${escapeHtml(doc.client_name || 'Unnamed Client')}</div>
          <div style="font-size:0.75rem; color:var(--color-text-muted); margin-top:2px;">${escapeHtml(doc.client_email || '')}</div>
        </td>
        <td class="cell-author" style="white-space: nowrap;">
          <span style="font-size:0.8125rem; font-weight:600; color:var(--color-dark-text); display:inline-flex; align-items:center; gap:5px;">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#662D91" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            ${escapeHtml(doc.created_by_name || 'Executive Director')}
          </span>
        </td>
        <td class="cell-date" style="white-space: nowrap; font-size:0.8125rem; color:var(--color-text-muted);">${doc.issue_date || '-'}</td>
        <td class="cell-total" style="white-space: nowrap; text-align:right;"><strong style="color:var(--brand-purple); font-size:0.9375rem;">$${parseFloat(doc.total_amount || 0).toFixed(2)}</strong></td>
        <td class="cell-actions" style="text-align:right; white-space:nowrap;">
          <div style="display:inline-flex; align-items:center; gap:5px; justify-content:flex-end;">
            <button class="btn-pill btn-secondary admin-edit-doc-btn" data-id="${doc.id}" style="padding:0.3rem 0.6rem; font-size:0.75rem; display:inline-flex; align-items:center; gap:4px; height:30px; border-radius:6px; white-space:nowrap;">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              <span>Edit / View</span>
            </button>
            <button class="btn-action-delete admin-delete-doc-btn" data-id="${doc.id}" data-docnum="${escapeHtml(doc.doc_number)}" title="Delete Document" style="width:30px; height:30px; min-width:30px; border-radius:6px; border:1px solid rgba(239, 68, 68, 0.25); background:rgba(239, 68, 68, 0.08); color:#EF4444; display:inline-flex; align-items:center; justify-content:center; cursor:pointer; transition:all 0.2s ease; padding:0;">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
            </button>
          </div>
        </td>
      `;

      docTableBody.appendChild(row);
    });

    // Attach row action listeners
    docTableBody.querySelectorAll('.admin-edit-doc-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = e.currentTarget.dataset.id;
        window.dispatchEvent(new CustomEvent('hba:open-invoice-editor', { detail: { id: id } }));
      });
    });

    docTableBody.querySelectorAll('.admin-delete-doc-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = e.currentTarget.dataset.id;
        const docNum = e.currentTarget.dataset.docnum || 'this document';
        openDeleteConfirmModal(id, docNum);
      });
    });
  }

  // In-Window Custom Delete Confirmation Modal Logic (Zero browser native popups!)
  const deleteConfirmModal = document.getElementById('admin-delete-confirm-modal');
  const deleteDocNumSpan = document.getElementById('delete-confirm-doc-number');
  const deleteCancelBtn = document.getElementById('delete-confirm-cancel-btn');
  const deleteActionBtn = document.getElementById('delete-confirm-action-btn');
  let pendingDeleteId = null;

  function openDeleteConfirmModal(id, docNum) {
    pendingDeleteId = id;
    if (deleteDocNumSpan) deleteDocNumSpan.textContent = docNum;
    if (deleteConfirmModal) deleteConfirmModal.classList.add('active');
  }

  function closeDeleteConfirmModal() {
    pendingDeleteId = null;
    if (deleteConfirmModal) deleteConfirmModal.classList.remove('active');
  }

  if (deleteCancelBtn) {
    deleteCancelBtn.addEventListener('click', closeDeleteConfirmModal);
  }

  if (deleteConfirmModal) {
    deleteConfirmModal.addEventListener('click', (e) => {
      if (e.target === deleteConfirmModal) closeDeleteConfirmModal();
    });
  }

  if (deleteActionBtn) {
    deleteActionBtn.addEventListener('click', async () => {
      if (!pendingDeleteId) return;
      const targetId = pendingDeleteId;
      closeDeleteConfirmModal();
      try {
        await fetch(`${INVOICE_API}?action=delete&id=${targetId}`, { method: 'POST' });
      } catch (err) {}
      loadDashboardDocuments();
    });
  }

  // Filter & Search Event Listeners
  if (searchInput) searchInput.addEventListener('input', () => renderDashboardTable(allDocumentsList));
  if (filterTypeSelect) filterTypeSelect.addEventListener('change', () => renderDashboardTable(allDocumentsList));

  // Action Buttons: + Create New Document / Settings
  const newDocBtn = document.getElementById('admin-new-doc-btn');
  if (newDocBtn) {
    newDocBtn.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('hba:open-invoice-editor', { detail: { type: 'quotation', newDoc: true } }));
    });
  }

  const settingsBtn = document.getElementById('admin-settings-btn') || document.getElementById('admin-open-settings-btn');
  if (settingsBtn) {
    settingsBtn.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('hba:open-settings'));
    });
  }

  // Listen for editor updates to refresh dashboard table automatically
  window.addEventListener('hba:invoice-saved-to-db', () => {
    loadDashboardDocuments();
  });
}

function getLocalMockDocuments() {
  return [
    { id: 1, doc_number: 'HBA-QUO-2026-8912', doc_type: 'quotation', client_name: 'Ministry of National Planning', client_email: 'procurement@planning.gov.so', issue_date: '2026-08-15', total_amount: 8500.00 },
    { id: 2, doc_number: 'HBA-INV-2026-4102', doc_type: 'invoice', client_name: 'East Africa Health Initiative', client_email: 'info@ea-health.org', issue_date: '2026-08-10', total_amount: 14200.00 }
  ];
}

function escapeHtml(str) {
  if (!str) return '';
  return str.replace(/[&<>"']/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[m]));
}
